import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { useCommandPalette } from '../../hooks/useCommandPalette';
import type { CommandResult } from '../../hooks/useCommandPalette';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string | undefined;
}

const TYPE_LABELS: Record<string, string> = {
  navigation: 'Navigation',
  action: 'Quick Actions',
  team_member: 'Team Members',
  invoice: 'Invoices',
  job: 'Jobs',
  booking: 'Bookings',
  settings: 'Settings',
};

const TYPE_ORDER = ['team_member', 'invoice', 'job', 'booking', 'settings', 'navigation', 'action'];

function groupResults(results: CommandResult[]): { label: string; items: CommandResult[] }[] {
  const map = new Map<string, CommandResult[]>();
  for (const r of results) {
    const arr = map.get(r.type) ?? [];
    arr.push(r);
    map.set(r.type, arr);
  }
  const groups: { label: string; items: CommandResult[] }[] = [];
  for (const type of TYPE_ORDER) {
    const items = map.get(type);
    if (items && items.length > 0) {
      groups.push({ label: TYPE_LABELS[type] ?? type, items });
    }
  }
  return groups;
}

export function CommandPalette({ isOpen, onClose, businessId }: CommandPaletteProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const { query, setQuery, results, loading, selectedIndex, setSelectedIndex } = useCommandPalette(businessId, isOpen);

  // Auto-focus input
  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 50);
  }, [isOpen]);

  const execute = useCallback((result: CommandResult) => {
    onClose();
    if (result.action && result.route) {
      navigate(`${result.route}?action=${result.action}`);
    } else if (result.route) {
      navigate(result.route);
    }
  }, [navigate, onClose]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(Math.min(selectedIndex + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(Math.max(selectedIndex - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      e.preventDefault();
      execute(results[selectedIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }, [selectedIndex, results, execute, onClose, setSelectedIndex]);

  if (!isOpen) return null;

  const groups = groupResults(results);

  // Compute flat index for highlighting
  let flatIndex = 0;
  function getFlatIndex() { return flatIndex++; }

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, zIndex: 60,
        background: 'rgba(0,0,0,0.4)',
      }} />

      {/* Palette */}
      <div style={{
        position: 'fixed', top: '20%', left: '50%', transform: 'translateX(-50%)',
        width: '100%', maxWidth: 560, zIndex: 61,
        background: palette.white, borderRadius: 12,
        border: `1px solid ${palette.g150}`,
        boxShadow: '0 16px 64px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.08)',
        maxHeight: '70vh', display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Search input */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '14px 16px', borderBottom: `1px solid ${palette.g100}`,
        }}>
          <Search size={18} color={palette.g400} style={{ flexShrink: 0 }} />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search team, invoices, jobs..."
            style={{
              flex: 1, border: 'none', outline: 'none',
              fontSize: 16, color: palette.g800, fontFamily: 'inherit',
              background: 'transparent',
            }}
          />
          {loading && <span style={{ fontSize: 11, color: palette.g400 }}>Searching...</span>}
          <span style={{
            fontSize: 10.5, color: palette.g300,
            padding: '2px 6px', border: `1px solid ${palette.g200}`, borderRadius: 4,
            flexShrink: 0,
          }}>ESC</span>
        </div>

        {/* Results */}
        <div style={{ overflowY: 'auto', padding: '8px 0' }}>
          {results.length === 0 && query.trim() && !loading ? (
            <div style={{ padding: '24px 16px', textAlign: 'center', color: palette.g400, fontSize: 13.5 }}>
              No results for "{query}"
            </div>
          ) : (
            groups.map(group => (
              <div key={group.label}>
                <div style={{
                  fontSize: 11, fontWeight: 600, color: palette.g400,
                  textTransform: 'uppercase', letterSpacing: '0.08em',
                  padding: '12px 16px 6px',
                }}>
                  {group.label}
                </div>
                {group.items.map(item => {
                  const idx = getFlatIndex();
                  const isSelected = idx === selectedIndex;
                  return (
                    <div
                      key={item.id}
                      onClick={() => execute(item)}
                      onMouseEnter={() => setSelectedIndex(idx)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '10px 16px', cursor: 'pointer',
                        background: isSelected ? palette.accentWash : 'transparent',
                        borderLeft: isSelected ? `3px solid ${palette.accent}` : '3px solid transparent',
                        transition: 'background 0.08s',
                      }}
                    >
                      <span style={{ fontSize: 15, width: 20, textAlign: 'center', flexShrink: 0, color: palette.g500 }}>
                        {item.icon}
                      </span>
                      <span style={{ fontSize: 14, fontWeight: 500, color: palette.g800, flex: 1 }}>
                        {item.title}
                      </span>
                      {item.subtitle && (
                        <span style={{ fontSize: 13, color: palette.g400, textTransform: 'capitalize' }}>
                          {item.subtitle}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}
